import { useEffect, useRef, useState } from 'react';
import { api, can, getUser } from '../../lib/api';
import { day, dayTime, shortList } from '../../lib/format';
import { Card, Empty, ErrorNote, Field, Loading, Modal, Pill, useResource, useToast } from '../../components/ui';
import { RowActions } from '../../components/RowActions';
import {
  MODE_LABEL, every, plural, type AlertRow, type PointRow, type ScanMode, type ScanRow, type ScannerStatus,
  type ScheduleRow, type UserRow,
} from './types';

/** Scheduled scans, the scan history, alerts raised by scans, and a manual scan. */
export default function Scans({
  points, reload, onChanged, adding, setAdding,
}: {
  points: PointRow[];
  reload: number;
  onChanged: () => void;
  adding: boolean;
  setAdding: (v: boolean) => void;
}) {
  const toast = useToast();
  const scanner = can(getUser(), 'cookies:scan');
  const schedules = useResource<{ schedules: ScheduleRow[] }>(() => api.get('/v1/cookie-schedules'), [reload]);
  const scans = useResource<{ scans: ScanRow[] }>(() => api.get('/v1/cookie-scans'), [reload]);
  const alerts = useResource<{ alerts: AlertRow[] }>(() => api.get('/v1/cookie-alerts'), [reload]);
  // Older API builds have no crawler; treat a failed status read as not deployed.
  const crawler = useResource<ScannerStatus>(() => api.get('/v1/cookie-scanner/status'), [reload]);
  const [editing, setEditing] = useState<ScheduleRow | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [opened, setOpened] = useState<ScanRow | null>(null);

  const scheduleRows = schedules.data?.schedules ?? [];
  const scanRows = scans.data?.scans ?? [];
  const alertRows = alerts.data?.alerts ?? [];
  const activeSchedules = scheduleRows.filter((s) => s.status === 'active');
  const nextRun = activeSchedules.reduce<string | null>((min, s) => (!min || s.nextRunAt < min ? s.nextRunAt : min), null);
  const cookiePoints = points.filter((p) => p.channel === 'cookie');
  const crawlerState = crawler.data?.status ?? (crawler.error ? 'not_deployed' : null);

  // Browser scans finish in the background: re-read while any is queued or
  // running, and refresh the other views once the last one completes.
  const pending = scanRows.some((sc) => sc.status === 'queued' || sc.status === 'running');
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !pending) onChanged();
    wasPending.current = pending;
    if (!pending) return;
    const t = setInterval(() => scans.reload(), 5_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const scheduleFor = (scan: ScanRow) =>
    scheduleRows.find((s) => (scan.scheduleId ? s.id === scan.scheduleId : s.lastScanId === scan.id)) ?? null;

  async function runNow(s: ScheduleRow) {
    setRunning(s.id);
    try {
      const res = await api.post<{ scan: ScanRow; alerted: boolean; queued?: boolean }>(`/v1/cookie-schedules/${s.id}/run`);
      toast(res.queued
        ? `${s.name}: crawl queued.`
        : `${s.name}: ${plural(res.scan.pagesFetched, 'page')} read · ${res.scan.newFindings} new${res.alerted ? ' · alert sent' : ''}`, 'ok');
      onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'bad');
    } finally {
      setRunning(null);
    }
  }

  async function setStatus(s: ScheduleRow, status: ScheduleRow['status']) {
    try {
      await api.patch(`/v1/cookie-schedules/${s.id}`, { status });
      toast(status === 'paused' ? `${s.name} paused.` : `${s.name} resumed.`, 'ok');
      onChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'bad');
    }
  }

  return (
    <>
      {schedules.data && (
        <p className="page-line">
          <strong>{activeSchedules.length}</strong> of {plural(scheduleRows.length, 'schedule')} active
          {nextRun && <> · next run {day(nextRun)}</>}
          {scanRows[0] && <> · last scan {day(scanRows[0].startedAt)}</>}
          {crawlerState && <> · <CrawlerLine state={crawlerState} status={crawler.data} /></>}
        </p>
      )}

      <Card title="Schedules">
        <ErrorNote error={schedules.error} />
        {schedules.loading && !schedules.data && <Loading what="Loading schedules" />}
        {schedules.data && scheduleRows.length === 0 && <Empty>No schedules.</Empty>}
        {scheduleRows.length > 0 && (
          <div className="table-scroll">
            <table className="data ck-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="num">Pages</th>
                  <th>Method</th>
                  <th>Every</th>
                  <th>Next run</th>
                  <th>Last run</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {scheduleRows.map((s) => (
                  <tr key={s.id}>
                    <td className="ck-cell"><strong>{s.name}</strong></td>
                    <td className="num" title={s.urls.join('\n')}>{s.urls.length}</td>
                    <td className="small">{MODE_LABEL[s.mode ?? 'html']}</td>
                    <td className="small">{every(s.everyDays)}</td>
                    <td className="small">{s.status === 'active' ? day(s.nextRunAt) : <span className="muted">—</span>}</td>
                    <td className="small">{s.lastRunAt ? day(s.lastRunAt) : <span className="muted">Never</span>}</td>
                    <td>{s.status === 'active' ? <span className="small muted">Active</span> : <Pill tone="warn">Paused</Pill>}</td>
                    <td className="u-actions">
                      {scanner && (
                        <RowActions
                          primary={
                            <button className="small" disabled={running !== null} onClick={() => runNow(s)}>
                              {running === s.id ? 'Running…' : 'Run now'}
                            </button>
                          }
                          more={[
                            { label: 'Edit', onClick: () => setEditing(s) },
                            s.status === 'active'
                              ? { label: 'Pause', onClick: () => setStatus(s, 'paused') }
                              : { label: 'Resume', onClick: () => setStatus(s, 'active') },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Scan history">
        <ErrorNote error={scans.error} />
        {scans.loading && !scans.data && <Loading what="Loading scans" />}
        {scans.data && scanRows.length === 0 && <Empty>No scans.</Empty>}
        {scanRows.length > 0 && (
          <div className="table-scroll">
            <table className="data ck-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Method</th>
                  <th className="num">Pages</th>
                  <th className="num">Found</th>
                  <th className="num">New</th>
                  <th>Run by</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {scanRows.map((sc) => {
                  const sched = scheduleFor(sc);
                  const open = sc.status === 'queued' || sc.status === 'running';
                  return (
                    <tr key={sc.id} className="ck-click" onClick={() => setOpened(sc)}>
                      <td className="small u-nowrap">{day(sc.startedAt)}</td>
                      <td className="small">{MODE_LABEL[sc.mode ?? 'html']}</td>
                      <td className="num">
                        {sc.status === 'queued' ? <span className="muted">—</span> : sc.pagesFetched}
                        {sc.pagesFailed > 0 && <span className="muted"> · {sc.pagesFailed} failed</span>}
                      </td>
                      <td className="num">{open ? <span className="muted">—</span> : sc.findings}</td>
                      <td className="num">{open ? <span className="muted">—</span> : sc.newFindings}</td>
                      <td className="small ck-cell">
                        {sched ? sched.name : sc.requestedBy ?? <span className="muted">—</span>}
                      </td>
                      <td><ScanStatus status={sc.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {alertRows.length > 0 && (
        <Card title="Alerts">
          <div className="table-scroll">
            <table className="data ck-table">
              <thead>
                <tr>
                  <th>Raised</th>
                  <th>Cookies</th>
                  <th>Sent to</th>
                  <th>Closed</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {alertRows.map((a) => (
                  <tr key={a.id}>
                    <td className="small u-nowrap">{day(a.createdAt)}</td>
                    <td className="small ck-cell" title={a.cookies.map((c) => `${c.name}${c.vendor ? ` (${c.vendor})` : ''}`).join('\n')}>
                      {shortList(a.cookies.map((c) => c.name), 3)}
                    </td>
                    <td className="small ck-cell" title={a.notified.join('\n')}>{shortList(a.notified, 2)}</td>
                    <td className="small">{a.closedAt ? day(a.closedAt) : <span className="muted">—</span>}</td>
                    <td>{a.status === 'open' ? <Pill tone="bad">Open</Pill> : <span className="small muted">Closed</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {scanner && (
        <ScanForm
          key={cookiePoints.map((p) => p.id).join()}
          points={cookiePoints}
          crawler={crawlerState === 'running' ? crawler.data : null}
          crawlerState={crawlerState}
          onDone={(msg) => { toast(msg, 'ok'); onChanged(); }}
        />
      )}

      {(adding || editing) && (
        <ScheduleModal
          schedule={editing}
          points={cookiePoints}
          crawlerState={crawlerState}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={(msg) => { setAdding(false); setEditing(null); toast(msg, 'ok'); onChanged(); }}
        />
      )}

      {opened && <ScanModal scan={opened} scheduleName={scheduleFor(opened)?.name ?? null} onClose={() => setOpened(null)} />}
    </>
  );
}

function ScanStatus({ status }: { status: string }) {
  if (status === 'complete') return <span className="small muted">Complete</span>;
  if (status === 'error') return <Pill tone="bad">Failed</Pill>;
  if (status === 'queued') return <Pill>Queued</Pill>;
  return <Pill tone="warn">Running</Pill>;
}

type CrawlerState = ScannerStatus['status'];

function CrawlerLine({ state, status }: { state: CrawlerState; status: ScannerStatus | null }) {
  if (state === 'running') {
    return <>Browser crawler: running{status && status.queued > 0 && <> · {status.queued} queued</>}</>;
  }
  if (state === 'stopped') return <>Browser crawler: not running since {day(status?.lastSeenAt ?? null)}</>;
  return <>Browser crawler: not deployed</>;
}

/** The disabled option names why, in the words of the status line. */
function crawlLabel(state: CrawlerState | null): string {
  if (state === 'running') return MODE_LABEL.browser;
  if (state === 'stopped') return `${MODE_LABEL.browser} · Browser crawler not running`;
  return `${MODE_LABEL.browser} · Browser crawler not deployed`;
}

const splitUrls = (text: string) => text.split(/[\s,]+/).map((u) => u.trim()).filter(Boolean);

/** The manual scan: same fetch a schedule runs, once. */
function ScanForm({
  points, crawler, crawlerState, onDone,
}: {
  points: PointRow[];
  crawler: ScannerStatus | null;
  crawlerState: CrawlerState | null;
  onDone: (summary: string) => void;
}) {
  const [urls, setUrls] = useState('');
  const [pointId, setPointId] = useState(points[0]?.id ?? '');
  const [mode, setMode] = useState<ScanMode>('html');
  const crawlable = crawlerState === 'running';
  const browser = mode === 'browser' && crawlable;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = splitUrls(urls);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ scan: ScanRow }>('/v1/cookie-scans', { urls: list, collectionPointId: pointId || null, mode: browser ? 'browser' : 'html' });
      setUrls('');
      onDone(browser
        ? 'Crawl queued.'
        : `${plural(res.scan.pagesFetched, 'page')} read · ${plural(res.scan.newFindings, 'new cookie')} to review.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Scan pages">
      <div className="ck-scan-form">
        <Field label="Pages" help={browser ? 'Start pages, one per line, up to 25.' : 'One address per line, up to 25.'}>
          <textarea
            rows={4}
            value={urls}
            placeholder={'https://www.example.in/\nhttps://www.example.in/membership'}
            onChange={(e) => setUrls(e.target.value)}
          />
        </Field>
        <div>
          <Field label="Method">
            <select value={browser ? 'browser' : 'html'} onChange={(e) => setMode(e.target.value as ScanMode)}>
              <option value="html">{MODE_LABEL.html}</option>
              <option value="browser" disabled={!crawlable}>{crawlLabel(crawlerState)}</option>
            </select>
          </Field>
          <Field label="Banner">
            <select value={pointId} onChange={(e) => setPointId(e.target.value)}>
              <option value="">No mapping</option>
              {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        </div>
      </div>
      <div className="ck-form-foot">
        <button className="primary" onClick={run} disabled={busy || !list.length || list.length > 25}>
          {busy ? (browser ? 'Queuing…' : 'Scanning…') : browser ? 'Queue crawl' : 'Run scan'}
        </button>
        <span className="small muted">
          {browser
            ? `Browser crawl: same-host links and sitemaps from these pages, up to ${plural(crawler?.maxPages ?? 100, 'page')}. Each page is loaded before and after accepting the banner.`
            : 'Static scan: HTML only. Tags loaded at runtime are reported by the banner.'}
        </span>
      </div>
      <ErrorNote error={error} />
    </Card>
  );
}

const EVERY_OPTIONS = [1, 7, 14, 30, 90];

function ScheduleModal({
  schedule, points, crawlerState, onClose, onSaved,
}: {
  schedule: ScheduleRow | null;
  points: PointRow[];
  crawlerState: CrawlerState | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  // Alert recipients must be console users. Listing them needs users:edit;
  // without it the addresses are typed and the API checks them.
  const users = useResource<{ users: UserRow[] }>(() => api.get('/v1/users'), []);
  const [name, setName] = useState(schedule?.name ?? '');
  const [pointId, setPointId] = useState(schedule ? schedule.collectionPointId ?? '' : points[0]?.id ?? '');
  const [urls, setUrls] = useState((schedule?.urls ?? []).join('\n'));
  const [everyDays, setEveryDays] = useState(schedule?.everyDays ?? 7);
  const [mode, setMode] = useState<ScanMode>(schedule?.mode ?? 'html');
  // A schedule already on browser crawl keeps the option, crawler or not.
  const crawlable = crawlerState === 'running' || schedule?.mode === 'browser';
  const [notify, setNotify] = useState<string[]>(schedule?.notify ?? []);
  const [notifyText, setNotifyText] = useState((schedule?.notify ?? []).join('\n'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userList = users.error ? null : users.data?.users.filter((u) => u.status === 'active') ?? null;
  const list = splitUrls(urls);
  const options = EVERY_OPTIONS.includes(everyDays) ? EVERY_OPTIONS : [...EVERY_OPTIONS, everyDays].sort((a, b) => a - b);
  const recipients = users.error ? splitUrls(notifyText) : notify;
  // Addresses saved earlier that are no longer active users still show, so they can be removed.
  const extra = userList ? notify.filter((e) => !userList.some((u) => u.email.toLowerCase() === e.toLowerCase())) : [];

  async function save() {
    setBusy(true);
    setError(null);
    const body = { name: name.trim(), collectionPointId: pointId || null, urls: list, everyDays, notify: recipients, mode };
    try {
      if (schedule) await api.patch(`/v1/cookie-schedules/${schedule.id}`, body);
      else await api.post('/v1/cookie-schedules', body);
      onSaved(schedule ? 'Schedule saved.' : 'Schedule added.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const toggle = (email: string, on: boolean) =>
    setNotify((n) => (on ? [...n, email] : n.filter((e) => e.toLowerCase() !== email.toLowerCase())));

  return (
    <Modal
      title={schedule ? 'Edit schedule' : 'Add schedule'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !name.trim() || !list.length || list.length > 25}>
            {busy ? 'Saving…' : schedule ? 'Save' : 'Add schedule'}
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Name"><input type="text" value={name} maxLength={120} placeholder="Main site" onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Every">
          <select value={everyDays} onChange={(e) => setEveryDays(Number(e.target.value))}>
            {options.map((d) => <option key={d} value={d}>{every(d)}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Pages" help="One address per line, up to 25.">
        <textarea rows={5} value={urls} placeholder={'https://www.example.in/\nhttps://www.example.in/membership'} onChange={(e) => setUrls(e.target.value)} />
      </Field>
      <div className="row">
        <Field label="Method">
          <select value={mode} onChange={(e) => setMode(e.target.value as ScanMode)}>
            <option value="html">{MODE_LABEL.html}</option>
            <option value="browser" disabled={!crawlable}>{crawlable ? MODE_LABEL.browser : crawlLabel(crawlerState)}</option>
          </select>
        </Field>
        <Field label="Banner">
          <select value={pointId} onChange={(e) => setPointId(e.target.value)}>
            <option value="">No mapping</option>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Alert recipients">
        {users.loading && !users.data && !users.error && <div className="small muted">Loading users…</div>}
        {userList && (
          <div className="ck-recipients">
            {userList.map((u) => (
              <label key={u.email} className="ck-check">
                <input type="checkbox" checked={notify.some((e) => e.toLowerCase() === u.email.toLowerCase())} onChange={(e) => toggle(u.email, e.target.checked)} />
                <span>{u.name || u.email}</span>
                {u.name && <span className="small muted">{u.email}</span>}
              </label>
            ))}
            {extra.map((email) => (
              <label key={email} className="ck-check">
                <input type="checkbox" checked onChange={() => toggle(email, false)} />
                <span>{email}</span>
                <span className="small muted">Not an active user</span>
              </label>
            ))}
            {!userList.length && !extra.length && <div className="small muted">No active users.</div>}
          </div>
        )}
        {users.error && (
          <textarea rows={3} value={notifyText} placeholder="Console user emails, one per line" onChange={(e) => setNotifyText(e.target.value)} />
        )}
      </Field>
      <ErrorNote error={error} />
    </Modal>
  );
}

function ScanModal({ scan, scheduleName, onClose }: { scan: ScanRow; scheduleName: string | null; onClose: () => void }) {
  const browser = scan.mode === 'browser';
  const results = scan.pageResults ?? [];
  const unattributed = [...new Set(results.flatMap((r) => r.unattributedHosts ?? []))].sort();
  return (
    <Modal
      wide
      title={`Scan · ${day(scan.startedAt)}`}
      onClose={onClose}
      footer={<button onClick={onClose}>Close</button>}
    >
      <dl className="kv ck-kv">
        <dt>Started</dt><dd>{dayTime(scan.startedAt)}</dd>
        <dt>Finished</dt><dd>{scan.finishedAt ? dayTime(scan.finishedAt) : '—'}</dd>
        <dt>Run by</dt><dd>{scheduleName ?? scan.requestedBy ?? '—'}</dd>
        <dt>Method</dt><dd>{MODE_LABEL[scan.mode ?? 'html']}</dd>
        <dt>Pages</dt><dd>{scan.pagesFetched} {browser ? 'crawled' : 'read'}{scan.pagesFailed > 0 ? ` · ${scan.pagesFailed} failed` : ''}</dd>
        <dt>Found</dt><dd>{scan.status === 'complete' ? `${scan.findings} · ${scan.newFindings} new` : '—'}</dd>
        <dt>Status</dt><dd><ScanStatus status={scan.status} />{scan.error && <span className="bad-text small"> {scan.error}</span>}</dd>
      </dl>
      {browser && results.length > 0 && (
        <div className="table-scroll ck-gap">
          <table className="data ck-table">
            <thead>
              <tr>
                <th>Page</th>
                <th className="num">Before consent</th>
                <th className="num">After</th>
                <th>Set before consent</th>
                <th>Accept control</th>
                <th>Recognised</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={`${r.url}-${i}`}>
                  <td className="mono ck-cell" title={r.url}>{r.url}</td>
                  {r.ok ? (
                    <>
                      <td className="num">{r.cookiesBefore ?? 0}</td>
                      <td className="num">{r.cookiesAfter ?? 0}</td>
                      <td className="small ck-cell" title={(r.beforeConsent ?? []).join('\n')}>
                        {(r.beforeConsent ?? []).length ? <span className="bad-text">{shortList(r.beforeConsent ?? [], 3)}</span> : <span className="muted">None</span>}
                      </td>
                      <td className="small ck-cell">{r.acceptedVia ?? <span className="muted">Not found</span>}</td>
                      <td className="small ck-cell" title={(r.vendors ?? []).join('\n')}>{r.vendors.length ? shortList(r.vendors, 3) : <span className="muted">Nothing</span>}</td>
                    </>
                  ) : (
                    <td colSpan={5} className="small"><span className="bad-text">{r.error ?? 'Failed'}</span></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {browser && unattributed.length > 0 && (
        <dl className="kv ck-kv">
          <dt>Unattributed hosts</dt>
          <dd className="mono small ck-hosts">{unattributed.join(' · ')}</dd>
        </dl>
      )}
      {!browser && results.length > 0 && (
        <div className="table-scroll ck-gap">
          <table className="data ck-table">
            <thead><tr><th>Page</th><th>Recognised</th><th>Tag manager</th></tr></thead>
            <tbody>
              {(scan.pageResults ?? []).map((r, i) => (
                <tr key={`${r.url}-${i}`}>
                  <td className="mono ck-cell" title={r.url}>{r.url}</td>
                  <td className="small ck-cell">
                    {r.ok ? (r.vendors.join(', ') || <span className="muted">Nothing</span>) : <span className="bad-text">{r.error ?? 'Failed'}</span>}
                  </td>
                  <td className="small">{r.tagContainer ? 'Present' : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
